// Localization is based on '@ngx-translate/core';
// Please be familiar with official documentations first => https://github.com/ngx-translate/core

import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { map, switchMap, catchError, tap, take } from 'rxjs/operators';
import { of, Subject, Observable } from 'rxjs';
import { CoreState } from 'src/app/core/store/reducers';
import { getEmployee } from 'src/app/core/store';
import { TranslationCacheService } from './translation-cache.service';

@Injectable({
    providedIn: 'root',
})
export class TranslationService implements OnDestroy {
    private destroy$ = new Subject<void>();

    constructor(
        private translate: TranslateService,
        private store: Store<CoreState>,
        private readonly translationCacheService: TranslationCacheService
    ) { }

    refreshLocale(): Observable<void> {
        return this.store.select(getEmployee).pipe(
            take(1),
            switchMap(employee => this.downloadAndUse(employee?.locale))
        );
    }

    downloadAndUse(locale: string): Observable<void> {
        if (!locale) {
            console.log(`[TranslationService] Locale not found, skipping download and use`);
            return of(void 0);
        }

        console.log(`[TranslationService] Start to download and use for locale: ${locale}`);

        return this.translationCacheService.synchronizeTranslationsForCountry(locale).pipe(
            switchMap(() => {
                return this.setLanguage(locale);
            }),
            catchError(error => {
                console.error(`[TranslationService] Error downloading and using locale ${locale}:`, error);
                return of(void 0);
            })
        );
    }

    private setLanguage(locale: string): Observable<void> {
        console.log(`[TranslationService] Applying locale : ${locale}`);

        return this.translate.use(locale).pipe(
            tap(() => {
                console.log(`[TranslationService] Locale ${locale} applied successfully`);
            }),
            map(() => void 0),
            catchError(error => {
                console.error(`[TranslationService] Error applying locale ${locale}:`, error);
                return of(void 0);
            })
        );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
